import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Users, BookOpen, CheckCircle, TrendingUp, AlertTriangle, Settings, ClipboardList } from 'lucide-react';

const API = process.env.REACT_APP_API_URL || '';

export default function AdminAssessmentDashboard() {
  const [stats,    setStats]    = useState(null);
  const [reports,  setReports]  = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState('');

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/api/admin/assessment/stats`),
      axios.get(`${API}/api/admin/assessment/reports`),
    ])
      .then(([s, r]) => { setStats(s.data); setReports(r.data); })
      .catch(() => setError('Failed to load assessment stats.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="py-12 text-center text-slate-500">Loading…</div>;
  if (error)   return <div className="py-12 text-center text-red-500">{error}</div>;

  const { question_bank = {}, warnings = [] } = stats;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Assessment System</h1>
          <p className="text-sm text-slate-500 mt-0.5">Recruitment aptitude test dashboard</p>
        </div>
        <div className="flex gap-2">
          <Link to="/portal/admin/assessment/config"
            className="flex items-center gap-1.5 px-4 h-9 rounded-lg border border-slate-200 text-slate-700 text-sm font-medium hover:bg-slate-50 transition">
            <Settings className="w-4 h-4" /> Config
          </Link>
          <Link to="/portal/admin/assessment/questions"
            className="flex items-center gap-1.5 px-4 h-9 rounded-lg text-white text-sm font-medium hover:brightness-110 transition"
            style={{ backgroundColor: '#1e73be' }}>
            <BookOpen className="w-4 h-4" /> Questions
          </Link>
        </div>
      </div>

      {/* Warnings */}
      {warnings.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-1">
          <div className="flex items-center gap-2 font-semibold text-amber-800 text-sm mb-1">
            <AlertTriangle className="w-4 h-4" /> Warnings
          </div>
          {warnings.map((w, i) => <p key={i} className="text-sm text-amber-700">• {w}</p>)}
        </div>
      )}

      {/* Stats grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={<BookOpen />} label="Total Questions" value={stats.total_questions}
          sub={`${stats.active_questions} active`} color="#1e73be" />
        <StatCard icon={<Users />} label="Total Candidates" value={stats.total_candidates}
          sub="registered" color="#2ea690" />
        <StatCard icon={<CheckCircle />} label="Tests Completed" value={stats.completed_tests}
          sub="submissions" color="#7c3aed" />
        <StatCard icon={<TrendingUp />} label="Avg Score" value={`${stats.avg_score}%`}
          sub="overall" color="#ea580c" />
      </div>

      {/* Question bank */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h3 className="font-semibold text-slate-800 mb-4">Question Bank Status</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { cat: 'English',  key: 'english',  color: '#1e73be' },
            { cat: 'Maths',    key: 'maths',    color: '#2ea690' },
            { cat: 'Logical',  key: 'logical',  color: '#7c3aed' },
            { cat: 'Reading',  key: 'reading',  color: '#ea580c' },
          ].map(({ cat, key, color }) => (
            <div key={key} className="p-4 rounded-xl border border-slate-100 bg-slate-50 text-center">
              <p className="text-2xl font-bold mb-1" style={{ color }}>
                {question_bank[key] ?? '—'}
              </p>
              <p className="text-xs text-slate-500">{cat} (active)</p>
            </div>
          ))}
        </div>
      </div>

      {/* Two columns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Recent tests */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-800">Recent Completions</h3>
            <Link to="/portal/admin/assessment/candidates"
              className="text-xs text-[#1e73be] hover:underline">View all →</Link>
          </div>
          {reports?.recent_tests?.length > 0 ? (
            <div className="space-y-2">
              {reports.recent_tests.slice(0, 8).map((t, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0">
                  <div>
                    <p className="text-sm font-medium text-slate-800">{t.full_name}</p>
                    <p className="text-xs text-slate-400">{t.department} • {t.designation_applied}</p>
                  </div>
                  <span className={`text-sm font-bold ${(t.overall_pct || 0) >= 75 ? 'text-green-600' : (t.overall_pct || 0) >= 50 ? 'text-amber-600' : 'text-red-500'}`}>
                    {t.overall_pct?.toFixed(1)}%
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-400 text-center py-4">No completed tests yet.</p>
          )}
        </div>

        {/* Department report */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="font-semibold text-slate-800 mb-4">Department Average Scores</h3>
          {reports?.department_reports?.length > 0 ? (
            <div className="space-y-3">
              {reports.department_reports.map((d, i) => (
                <div key={i}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="font-medium text-slate-700 capitalize">{d.department || 'Other'}</span>
                    <span className="text-slate-500">{d.count} candidates • {parseFloat(d.avg_pct || 0).toFixed(1)}%</span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${d.avg_pct || 0}%`, backgroundColor: '#1e73be' }} />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-400 text-center py-4">No data yet.</p>
          )}
        </div>
      </div>

      {/* Quick links */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {[
          { to: '/portal/admin/assessment/candidates', icon: <Users className="w-5 h-5" />, label: 'All Candidates', desc: 'View and manage candidate list' },
          { to: '/portal/admin/assessment/questions',  icon: <BookOpen className="w-5 h-5" />, label: 'Question Bank',  desc: 'Add, edit, delete questions' },
          { to: '/portal/admin/assessment/config',     icon: <Settings className="w-5 h-5" />, label: 'Test Config',    desc: 'Timer, scoring, difficulty' },
        ].map(({ to, icon, label, desc }) => (
          <Link key={to} to={to}
            className="flex items-center gap-3 p-4 bg-white rounded-xl border border-slate-200 hover:border-[#1e73be] hover:shadow-sm transition group">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center text-[#1e73be] group-hover:bg-[#1e73be] group-hover:text-white transition"
              style={{ backgroundColor: '#eaf3fb' }}>{icon}</div>
            <div>
              <p className="font-semibold text-slate-800 text-sm">{label}</p>
              <p className="text-xs text-slate-400">{desc}</p>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

const StatCard = ({ icon, label, value, sub, color }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-5">
    <div className="flex items-center gap-3 mb-2">
      <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: color + '18', color }}>
        {React.cloneElement(icon, { className: 'w-4 h-4' })}
      </div>
      <span className="text-xs text-slate-500 font-medium">{label}</span>
    </div>
    <p className="text-2xl font-bold text-slate-800">{value}</p>
    <p className="text-xs text-slate-400 mt-0.5">{sub}</p>
  </div>
);
